import { useMemo } from "react";

/**
 * A hook that filters a list of data based on the provided search predicate.
 * Does not mutate the data but returns a new copy. This is a "slice and dice" hook.
 *
 * When using with other "slice and dice" hooks, make sure to run this first for
 * optimal performance.
 *
 * @param data - data to search
 * @param searchPredicate - predicate to search the data
 * @returns filtered data and the total number of items and the number of hidden
 * items
 */
export const useSearch = <TData>(
  data: TData[] = [],
  searchPredicate: (data: TData) => boolean
) => {
  const filteredData = useMemo(
    () => data.filter(searchPredicate),
    [data, searchPredicate]
  );

  return {
    /** filtered data. non-mutated copy. */
    data: filteredData,
    /** total number of items */
    total: data.length,
    /** total number of hidden items after search applied */
    totalHidden: data.length - filteredData.length,
  };
};
